import styled from "@xstyled/styled-components";
import { useMutation } from "@apollo/client";
import { useEffect } from "react";

import { Silhouette } from "../../components/FormCardList";

import { Button, Spacing, Text } from "@otta/design";
import { SectionHeader } from "@otta/search/pages/Profile/components/FormHeader";
import { Icon } from "@otta/icons";
import {
  CreateUserVerificationSessionDocument,
  RedactAllUserVerificationSessionsDocument,
  ProfileCompletionStatusDocument,
} from "@otta/search/schema";
import { pushAnalyticsEvent } from "@otta/analytics";

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const BetaTag = styled(Text)`
  padding: xxs sm;
  margin: sm;
  background-color: blue-100;
  color: blue-800;
  border-radius: 4;
  font-weight: 600;
  font-size: 13;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content; center;
  gap: md;
`;

export const Verification = ({
  isVerified,
  canVerify,
}: {
  isVerified: boolean;
  canVerify: boolean;
}) => {
  const [createVerificationSession] = useMutation(
    CreateUserVerificationSessionDocument,
    {
      onCompleted: data => {
        const redirectUrl = data?.createUserVerificationSession?.url;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      },
    }
  );

  const [redactVerification, { loading: redactingLoading }] = useMutation(
    RedactAllUserVerificationSessionsDocument,
    { refetchQueries: [{ query: ProfileCompletionStatusDocument }] }
  );

  useEffect(() => {
    if (canVerify || isVerified) {
      pushAnalyticsEvent({
        eventName: "Candidate Viewed",
        name: "verification",
        page: "profile",
        isVerified: isVerified,
      });
    }
  }, [canVerify, isVerified]);

  if (!canVerify && !isVerified) {
    return null;
  }

  return (
    <div id="verification-form-id">
      <Spacing>
        <HeaderWrapper>
          <SectionHeader>Verification</SectionHeader>
          <BetaTag>BETA</BetaTag>
        </HeaderWrapper>
        <Silhouette>
          {isVerified ? (
            <Spacing>
              <Wrapper>
                <Text align="center" data-testid="empty-text">
                  You are successfully verified
                </Text>
                <Icon icon="verification" style={{ color: "#ACACFF" }} />
              </Wrapper>

              <Wrapper>
                <Button
                  level="destructive"
                  onClick={() => redactVerification()}
                  data-analytics-id="remove-verified-profile"
                  data-testid="removed-verified-button"
                >
                  {redactingLoading
                    ? "Removing your data..."
                    : "Remove verification"}
                </Button>
              </Wrapper>
            </Spacing>
          ) : (
            <Spacing>
              <Text align="center" data-testid="empty-text">
                Verification reassures companies that you are a real, authentic
                user.
              </Text>
              <Wrapper>
                <Button
                  level="secondary"
                  onClick={() => {
                    createVerificationSession({
                      variables: { returnPath: "/profile" },
                    });
                  }}
                  data-analytics-id="verify-candidate-profile"
                  data-testid="verify-account-button"
                >
                  Verify now
                </Button>
              </Wrapper>
            </Spacing>
          )}
        </Silhouette>
      </Spacing>
    </div>
  );
};
